// Consistent rendering of invoice status, flag chips, the aging pill, and the
// shared tinted dot-pill they build on (app.md §8).
import type { ReactNode } from 'react'
import { Badge, tokens } from '@fluentui/react-components'
import { InvoiceStatus, flagLabel, flagResolution } from '../lib/constants'
import { ageBand, flagPillLabel } from '../lib/format'
import { AGE_PILL_COLORS, TINT_PILLS, type PillColors } from '../lib/theme'

type BadgeColor =
  | 'brand'
  | 'danger'
  | 'important'
  | 'informative'
  | 'severe'
  | 'subtle'
  | 'success'
  | 'warning'

const STATUS_COLOR: Record<string, BadgeColor> = {
  [InvoiceStatus.PendingMatching]: 'subtle',
  [InvoiceStatus.PendingImportControl]: 'warning',
  [InvoiceStatus.PendingDepartment]: 'brand',
  [InvoiceStatus.PendingDirector]: 'brand',
  [InvoiceStatus.PendingOversight]: 'brand',
  [InvoiceStatus.PendingExportControl]: 'brand',
  [InvoiceStatus.PendingManualEntry]: 'brand',
  [InvoiceStatus.PendingDenialReview]: 'severe',
  [InvoiceStatus.PendingExport]: 'informative',
  [InvoiceStatus.BulkImported]: 'success',
  [InvoiceStatus.ManuallyEntered]: 'success',
  [InvoiceStatus.ArchivedWithoutPayment]: 'danger',
  [InvoiceStatus.ArchivedForFreshInvoice]: 'danger',
  [InvoiceStatus.ArchivedNotAnInvoice]: 'danger',
}

export function StatusBadge({ status }: { status: string | undefined }) {
  if (!status) return <Badge appearance="tint" color="subtle">Unknown</Badge>
  return (
    <Badge appearance="tint" color={STATUS_COLOR[status] ?? 'subtle'}>
      {status}
    </Badge>
  )
}

export function FlagChip({ flag }: { flag: string | undefined }) {
  const color: BadgeColor = flagResolution(flag) === 'auto' ? 'warning' : 'danger'
  return (
    <Badge appearance="tint" color={color} size="small">
      {flagLabel(flag)}
    </Badge>
  )
}

/** Tinted pill with a solid dot — the queue cards' shared chip shape. */
export function DotPill({ pill, title, children }: { pill: PillColors; title?: string; children: ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 10px 3px 8px',
        borderRadius: tokens.borderRadiusCircular,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        whiteSpace: 'nowrap',
        color: pill.fg,
        backgroundColor: pill.bg,
      }}
      title={title}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: pill.dot, flexShrink: 0 }} />
      {children}
    </span>
  )
}

/** "5 days" pill colored by aging band, green (fresh) → red (7+ days); always
    shows the day count so it reads without color. */
export function AgePill({ days }: { days: number | null }) {
  if (days === null) return <span style={{ color: tokens.colorNeutralForeground3 }}>—</span>
  return (
    <DotPill pill={AGE_PILL_COLORS[ageBand(days)]} title={`${days} day${days === 1 ? '' : 's'} since invoice date`}>
      {days} day{days === 1 ? '' : 's'}
    </DotPill>
  )
}

/**
 * "3 flags" (red) / "No flags" (green) — the Import Control and AP/AR flags
 * pill. The count is still the UNRESOLVED count; the word "unresolved" left
 * the pill because a resolved flag is not something the card needs to say
 * (owner, 2026-08-11). The tooltip still lists the flag labels.
 */
export function UnresolvedFlagsPill({ count, title }: { count: number; title?: string }) {
  return (
    <DotPill pill={count > 0 ? TINT_PILLS.red : TINT_PILLS.green} title={title}>
      {flagPillLabel(count)}
    </DotPill>
  )
}
